"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/marketing/ui/Button";
import { createClient } from "@/lib/supabase";

/**
 * The right-hand end of the marketing nav.
 *
 * Marketing pages are fully static (no cookie reads) so they stay cacheable —
 * which means the session can only be known in the browser. We therefore render
 * the signed-OUT pair first (correct for almost every visitor, and identical on
 * server and client so hydration matches), then swap to a single "Open Coach
 * Pen" once a session is found. Offering "Log in / Start free" to someone who is
 * already signed in was the actual bug; a split second of it is the price of
 * keeping these pages static.
 *
 * The slot reserves its width so the nav items to its left cannot jump when the
 * swap happens, and the signed-in link fades in so it reads as intentional.
 */
export function NavAuthCta() {
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    let live = true;
    // getSession reads the local store — no network round trip on a page whose
    // whole point is to be fast.
    supabase.auth.getSession().then(({ data }) => {
      if (live) setSignedIn(!!data.session);
    });
    // Keep up with a sign-out (or sign-in) that happens in another tab.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (live) setSignedIn(!!session);
    });
    return () => {
      live = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return (
    <span className="inline-flex min-w-[9.5rem] items-center justify-end gap-1 sm:min-w-[11rem] sm:gap-5">
      {signedIn ? (
        <Button href="/app" size="sm" className="reveal-fast">
          Open Coach Pen →
        </Button>
      ) : (
        <>
          <Link
            href="/auth/login"
            className="rounded-full px-3 py-2 text-ink-soft transition-colors hover:text-ink"
          >
            Log in
          </Link>
          <Button href="/auth/signup" size="sm">
            Start free
          </Button>
        </>
      )}
    </span>
  );
}
